import {
  ColumnType,
  ConditionType,
  PropertyComparisonType,
  type RawColumn,
  UnknownOperator,
} from "src/types/visual";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { PropertyFilterProps } from "./property-filter";
import {
  createSubconditionUpdater,
  removeSubconditionMutative,
} from "src/components/explore/utils/condition-builders";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { AddBoxIcon, Column, Tooltip } from "@hightouch/htui";
import { NestedPropertyFilter } from "./nested-property-filter";
import { DetailButton } from "./condition-buttons";
import { isJsonPathColumn } from "@hightouch/lib/query/visual/types";

interface PropertyFilterJsonSubconditionsProps extends PropertyFilterProps {
  pl: number;
  isRealtimeAudience: boolean;
}

const initialJsonPathCondition = (column: RawColumn) => ({
  id: crypto.randomUUID(),
  type: ConditionType.Property as const,
  propertyType: ColumnType.Json,
  property: {
    type: "json_path" as const,
    castType: ColumnType.Unknown,
    column,
    path: "",
  },
  operator: UnknownOperator.Exists,
  value: null,
});

export const PropertyFilterJsonSubconditions = (
  props: PropertyFilterJsonSubconditionsProps,
) => {
  const { condition, onUpdate, pl, isRealtimeAudience } = props;
  const { queryBuilderJsonFilters } = useFlags();
  const topLevelSubcondition = condition.subconditions?.[0] ?? {
    id: crypto.randomUUID(),
    type: ConditionType.And as const,
    conditions: [],
  };
  const subconditions =
    // Need to handle legacy issues where defunct subconditions were preserved when swapping from an
    // event/related model condition to a property condition
    topLevelSubcondition?.conditions?.filter((c) =>
      isJsonPathColumn(c.property),
    ) ?? [];
  const addSubcondition = () => {
    onUpdate((condition) => {
      condition.subconditions = [
        {
          type: topLevelSubcondition?.type,
          id: topLevelSubcondition.id,
          conditions: [
            ...topLevelSubcondition.conditions,
            initialJsonPathCondition(condition.property as RawColumn),
          ],
        },
      ];
    });
  };

  const generateUpdater = createSubconditionUpdater(onUpdate);

  const removeSubcondition = (id?: string) => () =>
    onUpdate((condition) => removeSubconditionMutative(condition, id));

  const updateWrappingConditionType = (
    type: ConditionType.And | ConditionType.Or,
  ) => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    onUpdate((condition) => {
      condition.subconditions = [
        {
          type: type,
          id: topLevelSubcondition.id,
          conditions: topLevelSubcondition?.conditions ?? [],
        },
      ];
    });
  };

  return (
    <Column pl={pl} gap={4}>
      {subconditions.map((s, index) => (
        <NestedPropertyFilter
          key={s.id ?? index}
          {...props}
          lhsPropertyFilterVariant="text"
          condition={s}
          propertyComparisonType={PropertyComparisonType.None}
          isFirstCondition={index === 0}
          // TODO: check check on property comparison
          columns={undefined}
          // includeParentInPropertyComparison
          onUpdate={generateUpdater(s.id)}
          onRemove={removeSubcondition(s.id)}
          isWrappedWithAndCondition={
            topLevelSubcondition?.type === ConditionType.And
          }
          onToggleWrappingConditionType={updateWrappingConditionType}
        />
      ))}
      {queryBuilderJsonFilters && (
        <Tooltip
          isDisabled={!isRealtimeAudience}
          message="JSON subconditions are not supported for realtime audiences"
        >
          <DetailButton
            isDisabled={props.isDisabled || isRealtimeAudience}
            icon={AddBoxIcon}
            size="sm"
            onClick={addSubcondition}
          >
            Where JSON key is…
          </DetailButton>
        </Tooltip>
      )}
    </Column>
  );
};
